import type { i18n } from 'i18next';
import i18next from 'i18next';
import { storeToRefs } from 'pinia';

import { EnumMagicStrings } from '@/helpers/enums/misc.enum';
import { pinia } from '@/pinia';
import { useLocaleStore } from '@/stores/locale.store';
import { useOrganizationStore } from '@/stores/organzation.pinia';
import { useVuetifyStore } from '@/stores/vuetify.pinia';

export async function initializeI18n(): Promise<void | i18n> {
  const url = window.location.href;
  const lang = url.split('lang=')[1];
  const initialLang =
    lang == 'ar' || lang == 'en'
      ? lang
      : localStorage.getItem(EnumMagicStrings.localeLanguage) || 'en';
  return await i18next
    .init({
      // debug: true,
      fallbackLng: initialLang,
      preload: ['en', 'ar'],
      initImmediate: true,
      lng: initialLang, // Set initial language
      resources: {
        en: {
          translation: {
            header: {
              internalTeam: 'Corporate Mode',
              // Fallback label for the consolidated internal-team nav entry
              // (KP-68); the org's CMS dashboard terminology wins when loaded
              // (header-lists.enum.ts).
              dashboard: 'Dashboard',
            },
            internalTeam: {
              title: 'Corporate Mode',
              tabs: {
                overview: 'Overview',
                journey: 'Journey',
                myModules: 'My Modules',
                progress: 'Progress',
                profile: 'Profile',
                questions: 'Questions',
                library: 'Library',
              },
              library: {
                intro:
                  'Reference material to look things up after you finish a module — policies, templates, terms and the org chart.',
                sections: {
                  policies: 'Policies',
                  templates: 'Templates',
                  glossary: 'Glossary',
                  orgChart: 'Org chart',
                },
                emptySection: 'Content coming from HR.',
                open: 'Open',
                loadFailed:
                  "We couldn't load the library content. Please try again later.",
                achievements: {
                  title: 'My record',
                  subtitle:
                    'Modules you have completed, with your best exam scores.',
                  completedOn: 'Completed {{date}}',
                  bestScore: 'Best score: {{score}} of {{max}} ({{percent}}%)',
                  empty: 'No completed modules yet.',
                  loadFailed:
                    "We couldn't load your record. Please try again later.",
                },
              },
              progress: {
                title: 'Module-by-module progress',
                hint: 'Your completion, scores, and the conversation with your training team — for every module, in one place.',
                loadFailed:
                  "We couldn't load your progress. Please try again later.",
                empty: 'No modules are assigned to you yet.',
                completedXofY: '{{completed}} of {{total}} items completed',
                scoresTitle: 'Scores',
                scoresLoadFailed:
                  "We couldn't load the scores for this module.",
                scoresEmpty: 'No scored items in this module yet.',
                scoresNoCourses:
                  'This module has no linked course, so there are no scores to show.',
                scoreOutOf: '{{achieved}} of {{max}} points',
                scoreNotGraded: 'Not graded yet',
                passMark: 'Pass mark: {{passMark}}%',
                threadTitle: 'Conversation with your training team',
                threadHint:
                  'Messages you send here reach your training team; their answers appear in the same thread.',
                threadEmpty:
                  'No messages about this module yet — send the first one below.',
                awaitingAnswer: 'Awaiting answer',
                youLabel: 'You',
                supervisorLabel: 'Training team',
                composerTitle: 'Send a message',
                composerItemLabel: 'Which item is this about?',
              },
              questions: {
                formTitle: 'Ask a question',
                formHint:
                  'Send a question to your training team — the answer will appear here once it is ready.',
                subjectLabel: 'Subject (optional)',
                bodyLabel: 'Your question',
                submit: 'Send question',
                submitted: 'Your question was sent.',
                submitFailed:
                  "We couldn't send your question. Please try again.",
                close: 'Close',
                aboutItem: 'About: {{title}}',
                listTitle: 'My questions',
                empty: "You haven't asked any questions yet.",
                loadFailed:
                  "We couldn't load your questions. Please try again later.",
                answerLabel: 'Answer',
                status: {
                  open: 'Open',
                  answered: 'Answered',
                },
                errors: {
                  bodyRequired: 'Please write your question first.',
                  subjectTooLong: 'Keep the subject under {{max}} characters.',
                },
              },
              rating: {
                rateModule: 'Rate this module',
                dialogTitle: 'Rate "{{name}}"',
                starsLabel: 'Your rating',
                commentLabel: 'Comment (optional)',
                submit: 'Submit rating',
                cancel: 'Cancel',
                submitFailed: "We couldn't save your rating. Please try again.",
                updateHint:
                  'You can change your rating anytime — the latest one counts.',
                errors: {
                  commentTooLong: 'Keep the comment under {{max}} characters.',
                },
              },
              profile: {
                editName: 'Edit name',
                firstName: 'First name',
                lastName: 'Last name',
                save: 'Save',
                cancel: 'Cancel',
                updateFailed:
                  "We couldn't save your changes. Please try again.",
                loadFailed:
                  "We couldn't load this section. Please try again later.",
                hrManaged: 'Managed by HR',
                notProvided: 'Not available yet',
                sections: {
                  organization: 'Organization details',
                  assessments: 'Assessment results',
                  education: 'Education & experience',
                  documents: 'HR documents',
                },
                assessmentsHint:
                  'Your answers from the Know Yourself onboarding step — used for team placement.',
                assessmentsEmpty: 'No assessment results yet.',
                educationEmpty:
                  'No education or experience records yet — managed by HR.',
                documentsEmpty: 'Managed by HR — nothing uploaded yet.',
                fields: {
                  role: 'Role',
                  department: 'Department',
                  startDate: 'Start date',
                  academicLevel: 'Academic level',
                  institute: 'Institute',
                  specialty: 'Specialty',
                  occupation: 'Occupation',
                  workPlace: 'Workplace',
                  yearsOfExperience: 'Years of experience',
                },
                documents: {
                  contracts: 'Contracts',
                  certificates: 'Certificates',
                  identity: 'Identity documents',
                },
              },
              empty: 'Nothing here yet.',
              comingSoon:
                'Coming soon \u2014 this area is under active development and will be enhanced continuously.',
              overview: {
                title: 'Your onboarding journey',
                stepLabel: 'Step {{number}}: {{title}}',
                done: 'Completed',
                current: 'In progress',
                upcoming: 'Not started yet',
                resume: 'Resume',
                complete: 'Onboarding complete',
                completeSubtitle: 'You have finished every onboarding step.',
              },
              journey: {
                gaugeTitle: 'My Journey',
                completedXofY:
                  '{{completed}} of {{total}} {{coursesText}} completed',
                completionNote:
                  'A {{courseText}} is completed when you finish and pass it.',
                notStarted: 'Not started',
                inProgress: 'In progress',
                completed: 'Completed',
                failed: 'Failed',
                resume: 'Resume',
                startLearning: 'Start learning',
                certificatesEarned: '{{count}} certificate(s) earned',
                certificatesLocked:
                  'Complete a {{courseText}} to earn its certificate.',
                certificatesAvailable: 'Your certificates are ready to view.',
                viewCertificates: 'View certificates',
              },
            },
            course: {
              completedSubsectionsCount:
                '{{completed}} of {{total}} {{modulesText}} completed',
              notEnrolled: 'You are not enrolled in this {{courseText}}.',
            },
            shared: {
              switchToSuperAdmin: 'Switch to Super Admin',
            },
            journeyItem: {
              autoCompleteHint:
                'Completed automatically when you finish the {{courseText}}.',
              customUserHint: "Tick when you've completed this.",
              customAdminHint: 'Your supervisor marks this one.',
              currentChip: 'Current',
              askQuestion: 'Ask a question about this item',
              lockedGeneric: 'Locked — complete the previous modules first.',
              lockedRequirement: 'Complete "{{title}}" to unlock this.',
              lockedRequirementWithPassMark:
                'Complete "{{title}}" (pass mark {{passMark}}%) to unlock this.',
            },
            journeyPlanner: {
              lockedJourney: 'Complete "{{name}}" to unlock this journey.',
              lockedJourneyGeneric:
                'Locked — complete the prerequisite journey first.',
              viewList: 'List',
              viewBoard: 'Board',
              viewListAria: 'Switch to list view',
              viewBoardAria: 'Switch to board view',
              viewToggleAria: 'Journey view mode',
              progressOfTotal: '{{completed}} of {{total}} completed',
              youAreHere: 'You are here',
              finishLabel: 'Finish',
            },
            gamification: {
              totalPoints: '{{points}} pts',
              pointsChip: '+{{points}}',
              journeyBonusChip: '+{{points}} journey bonus',
              rewardsTitle: 'My rewards',
              rewardsSubtitle:
                'Points and badges you have earned across your journeys.',
              badgeEarned: 'Earned',
              badgeLocked: 'Not earned yet',
              celebrationTitle: 'Journey complete!',
              celebrationBonus: '+{{points}} bonus points',
              badges: {
                firstStep: {
                  title: 'First Step',
                  description: 'Complete your first journey item.',
                },
                halfway: {
                  title: 'Halfway There',
                  description: 'Complete half of all your journey items.',
                },
                journeyComplete: {
                  title: 'Journey Complete',
                  description: 'Finish every item in a journey.',
                },
                allJourneys: {
                  title: 'All Journeys Complete',
                  description: 'Finish every journey assigned to you.',
                },
                highAchiever: {
                  title: 'High Achiever',
                  description: 'Keep an average exam score of 85% or higher.',
                },
              },
            },
            quiz: {
              passMarkRequired:
                'Pass mark: {{passMark}}% — you need to score at least {{passMark}}% to pass this exam.',
            },
            onboarding: {
              stepOfTotal: 'Step {{current}} of {{total}}',
              continue: 'Continue',
              later: 'Later',
              loading: 'Loading your onboarding journey…',
              noSteps: 'No onboarding steps are set up yet.',
              loadFailed:
                "We couldn't load your onboarding journey. Please try again.",
              retry: 'Retry',
              video: {
                unavailable:
                  'This video is not available right now — you can continue once it is ready.',
              },
              placeholder: {
                defaultTitle: 'Onboarding step',
                defaultDescription:
                  'This step will be available soon. You can continue for now.',
              },
              completion: {
                title: "You're all set!",
                subtitle: 'You have completed your onboarding journey.',
                certificateLabel: 'Certificate of completion',
                cta: 'Your next module is ready',
              },
              carousel: {
                watchedCount: '{{count}} of {{total}} watched',
              },
              cardsSign: {
                cardOf: 'Card {{current}} of {{total}}',
                back: 'Back',
                next: 'Next',
                signTitle: 'Sign to confirm',
                nameLabel: 'Full name',
                attestLabel: 'I have read and agree to these principles.',
                signAndContinue: 'Sign & Continue',
              },
              cardsAccept: {
                cardOf: 'Card {{current}} of {{total}}',
                back: 'Back',
                next: 'Next',
                groupAcceptTitle: '{{group}}',
                acceptLabel: 'I have read and accept the {{group}}.',
              },
              quiz: {
                preparingTitle: 'Your welcome challenge is being prepared',
                preparingDescription:
                  "This quiz isn't ready yet — you can continue for now and take it later.",
                readyTitle: 'Ready for a quick challenge?',
                passMark:
                  "You'll need {{passMark}}% to pass — retakes are allowed.",
                start: 'Start Quiz',
                encouragement:
                  "Don't worry — you can try again as many times as you need.",
              },
              assessment: {
                preparingTitle: 'Discover your strengths — coming soon',
                defaultDescription:
                  'This assessment will be available soon. You can continue for now.',
                passMark:
                  "You'll need {{passMark}}% to pass — retakes are allowed.",
                failedTitle: 'Not quite — give it another try',
                failedScore:
                  'You scored {{score}}% — you need {{passMark}}% to pass.',
                questionCorrect: 'Correct',
                questionIncorrect: 'Review this question',
                retake: 'Try again',
              },
              journey: {
                progress: '{{completed}} of {{total}} items complete',
                open: 'Go to journey',
                checkCompletion: 'Check completion',
                incomplete:
                  'Finish every item in this journey to complete this step.',
                completeReady: 'Journey complete — you can continue.',
              },
              certificate: {
                title: 'Onboarding Complete',
                cta: 'Claim your certificate',
              },
            },
            remediation: {
              title: 'Want a hand with this one?',
              body: 'Everyone hits a bump now and then. Get a short study pointer based on this attempt to help you focus your next try.',
              cta: 'Get guidance',
              caption: 'Generated guidance — double-check anything important.',
              limited:
                "You've asked for guidance a few times recently — please try again a little later.",
            },
          },
        },
        ar: {
          translation: {
            header: {
              internalTeam: 'الوضع المؤسسي',
              // Fallback label for the consolidated internal-team nav entry
              // (KP-68); the org's CMS dashboard terminology wins when loaded
              // (header-lists.enum.ts).
              dashboard: 'لوحة التحكم',
            },
            internalTeam: {
              title: 'الوضع المؤسسي',
              tabs: {
                overview: 'نظرة عامة',
                journey: 'الرحلة',
                myModules: 'وحداتي',
                progress: 'التقدم',
                profile: 'الملف الشخصي',
                questions: 'الأسئلة',
                library: 'المكتبة',
              },
              library: {
                intro:
                  'مواد مرجعية للرجوع إليها بعد إنهاء الوحدة — السياسات والنماذج والمصطلحات والهيكل التنظيمي.',
                sections: {
                  policies: 'السياسات',
                  templates: 'النماذج',
                  glossary: 'المصطلحات',
                  orgChart: 'الهيكل التنظيمي',
                },
                emptySection: 'المحتوى قادم من الموارد البشرية.',
                open: 'فتح',
                loadFailed: 'تعذّر تحميل محتوى المكتبة. يرجى المحاولة لاحقاً.',
                achievements: {
                  title: 'سجلي',
                  subtitle: 'الوحدات التي أكملتها مع أفضل درجات اختباراتك.',
                  completedOn: 'اكتملت في {{date}}',
                  bestScore: 'أفضل درجة: {{score}} من {{max}} ({{percent}}%)',
                  empty: 'لا توجد وحدات مكتملة بعد.',
                  loadFailed: 'تعذّر تحميل سجلك. يرجى المحاولة لاحقاً.',
                },
              },
              progress: {
                title: 'التقدم وحدةً بوحدة',
                hint: 'إنجازك ودرجاتك ومحادثتك مع فريق التدريب — لكل وحدة، في مكان واحد.',
                loadFailed: 'تعذّر تحميل تقدمك. يرجى المحاولة لاحقاً.',
                empty: 'لا توجد وحدات مسندة إليك بعد.',
                completedXofY: 'اكتمل {{completed}} من {{total}} عنصراً',
                scoresTitle: 'الدرجات',
                scoresLoadFailed: 'تعذّر تحميل درجات هذه الوحدة.',
                scoresEmpty: 'لا توجد عناصر مُقيَّمة في هذه الوحدة بعد.',
                scoresNoCourses:
                  'لا ترتبط هذه الوحدة بأي دورة، لذا لا توجد درجات لعرضها.',
                scoreOutOf: '{{achieved}} من {{max}} نقطة',
                scoreNotGraded: 'لم يتم التقييم بعد',
                passMark: 'درجة النجاح: {{passMark}}%',
                threadTitle: 'المحادثة مع فريق التدريب',
                threadHint:
                  'الرسائل التي ترسلها هنا تصل إلى فريق التدريب، وتظهر إجاباتهم في السلسلة نفسها.',
                threadEmpty:
                  'لا توجد رسائل حول هذه الوحدة بعد — أرسل أول رسالة أدناه.',
                awaitingAnswer: 'بانتظار الإجابة',
                youLabel: 'أنت',
                supervisorLabel: 'فريق التدريب',
                composerTitle: 'أرسل رسالة',
                composerItemLabel: 'أي عنصر تدور حوله رسالتك؟',
              },
              questions: {
                formTitle: 'اطرح سؤالاً',
                formHint:
                  'أرسل سؤالك إلى فريق التدريب — ستظهر الإجابة هنا فور جاهزيتها.',
                subjectLabel: 'الموضوع (اختياري)',
                bodyLabel: 'سؤالك',
                submit: 'إرسال السؤال',
                submitted: 'تم إرسال سؤالك.',
                submitFailed: 'تعذّر إرسال سؤالك. يرجى المحاولة مرة أخرى.',
                close: 'إغلاق',
                aboutItem: 'بخصوص: {{title}}',
                listTitle: 'أسئلتي',
                empty: 'لم تطرح أي أسئلة بعد.',
                loadFailed: 'تعذّر تحميل أسئلتك. يرجى المحاولة لاحقاً.',
                answerLabel: 'الإجابة',
                status: {
                  open: 'مفتوح',
                  answered: 'تمت الإجابة',
                },
                errors: {
                  bodyRequired: 'يرجى كتابة سؤالك أولاً.',
                  subjectTooLong: 'يجب ألا يتجاوز الموضوع {{max}} حرفاً.',
                },
              },
              rating: {
                rateModule: 'قيّم هذه الوحدة',
                dialogTitle: 'قيّم "{{name}}"',
                starsLabel: 'تقييمك',
                commentLabel: 'تعليق (اختياري)',
                submit: 'إرسال التقييم',
                cancel: 'إلغاء',
                submitFailed: 'تعذّر حفظ تقييمك. يرجى المحاولة مرة أخرى.',
                updateHint:
                  'يمكنك تغيير تقييمك في أي وقت — التقييم الأخير هو المعتمد.',
                errors: {
                  commentTooLong: 'يجب ألا يتجاوز التعليق {{max}} حرفاً.',
                },
              },
              profile: {
                editName: 'تعديل الاسم',
                firstName: 'الاسم الأول',
                lastName: 'اسم العائلة',
                save: 'حفظ',
                cancel: 'إلغاء',
                updateFailed: 'تعذّر حفظ التغييرات. يرجى المحاولة مرة أخرى.',
                loadFailed: 'تعذّر تحميل هذا القسم. يرجى المحاولة لاحقاً.',
                hrManaged: 'تُدار من الموارد البشرية',
                notProvided: 'غير متوفر بعد',
                sections: {
                  organization: 'بيانات المنظمة',
                  assessments: 'نتائج التقييم',
                  education: 'التعليم والخبرة',
                  documents: 'مستندات الموارد البشرية',
                },
                assessmentsHint:
                  'إجاباتك من خطوة "اعرف نفسك" في رحلة الإلحاق — تُستخدم لتحديد الفريق المناسب.',
                assessmentsEmpty: 'لا توجد نتائج تقييم بعد.',
                educationEmpty:
                  'لا توجد سجلات تعليم أو خبرة بعد — تُدار من الموارد البشرية.',
                documentsEmpty:
                  'تُدار من الموارد البشرية — لم يتم رفع أي مستند بعد.',
                fields: {
                  role: 'الدور الوظيفي',
                  department: 'القسم',
                  startDate: 'تاريخ المباشرة',
                  academicLevel: 'المستوى الأكاديمي',
                  institute: 'الجهة التعليمية',
                  specialty: 'التخصص',
                  occupation: 'المهنة',
                  workPlace: 'جهة العمل',
                  yearsOfExperience: 'سنوات الخبرة',
                },
                documents: {
                  contracts: 'العقود',
                  certificates: 'الشهادات',
                  identity: 'وثائق الهوية',
                },
              },
              empty: 'لا يوجد شيء هنا بعد.',
              comingSoon:
                'قريباً — هذه المنطقة قيد التطوير النشط وسيتم تحسينها باستمرار.',
              overview: {
                title: 'رحلة الإلحاق الخاصة بك',
                stepLabel: 'الخطوة {{number}}: {{title}}',
                done: 'مكتملة',
                current: 'قيد التنفيذ',
                upcoming: 'لم تبدأ بعد',
                resume: 'استئناف',
                complete: 'اكتملت رحلة الإلحاق',
                completeSubtitle: 'لقد أنهيت جميع خطوات الإلحاق.',
              },
              journey: {
                gaugeTitle: 'رحلتي',
                completedXofY:
                  '{{completed}} من {{total}} {{coursesText}} مكتملة',
                completionNote:
                  'تُحتسب {{courseText}} مكتملة عند إنهائها واجتيازها.',
                notStarted: 'لم تبدأ',
                inProgress: 'قيد التنفيذ',
                completed: 'مكتملة',
                failed: 'لم يتم اجتيازها',
                resume: 'استئناف',
                startLearning: 'ابدأ التعلم',
                certificatesEarned: '{{count}} شهادة مكتسبة',
                certificatesLocked: 'أكمل {{courseText}} للحصول على شهادتها.',
                certificatesAvailable: 'شهاداتك جاهزة للعرض.',
                viewCertificates: 'عرض الشهادات',
              },
            },
            course: {
              completedSubsectionsCount:
                '{{completed}} من {{total}} {{modulesText}} مكتملة',
              notEnrolled: 'أنت غير مسجل في {{courseText}}.',
            },
            shared: {
              switchToSuperAdmin: 'الانتقال إلى مدير النظام',
            },
            journeyItem: {
              autoCompleteHint:
                'يتم الإكمال تلقائياً عند إنهاء {{courseText}}.',
              customUserHint: 'ضع علامة عند إكمالك لهذا العنصر.',
              customAdminHint: 'يقوم مشرفك بتحديد هذا العنصر عند إكماله.',
              currentChip: 'الحالية',
              askQuestion: 'اطرح سؤالاً حول هذا العنصر',
              lockedGeneric: 'مقفلة — أكمل الوحدات السابقة أولاً.',
              lockedRequirement: 'أكمل "{{title}}" لفتح هذه الوحدة.',
              lockedRequirementWithPassMark:
                'أكمل "{{title}}" (درجة النجاح {{passMark}}%) لفتح هذه الوحدة.',
            },
            journeyPlanner: {
              lockedJourney: 'أكمل "{{name}}" لفتح هذه الرحلة.',
              lockedJourneyGeneric: 'مقفلة — أكمل الرحلة المتطلبة أولاً.',
              viewList: 'قائمة',
              viewBoard: 'لوحة',
              viewListAria: 'التبديل إلى عرض القائمة',
              viewBoardAria: 'التبديل إلى عرض اللوحة',
              viewToggleAria: 'طريقة عرض الرحلة',
              progressOfTotal: 'اكتمل {{completed}} من {{total}}',
              youAreHere: 'أنت هنا',
              finishLabel: 'النهاية',
            },
            gamification: {
              totalPoints: '{{points}} نقطة',
              pointsChip: '+{{points}}',
              journeyBonusChip: 'مكافأة الرحلة +{{points}}',
              rewardsTitle: 'مكافآتي',
              rewardsSubtitle: 'النقاط والأوسمة التي حصلت عليها خلال رحلاتك.',
              badgeEarned: 'تم الحصول عليه',
              badgeLocked: 'لم يتم الحصول عليه بعد',
              celebrationTitle: 'اكتملت الرحلة!',
              celebrationBonus: '+{{points}} نقطة إضافية',
              badges: {
                firstStep: {
                  title: 'الخطوة الأولى',
                  description: 'أكمل أول عنصر في رحلتك.',
                },
                halfway: {
                  title: 'منتصف الطريق',
                  description: 'أكمل نصف عناصر رحلاتك.',
                },
                journeyComplete: {
                  title: 'رحلة مكتملة',
                  description: 'أنهِ جميع عناصر إحدى الرحلات.',
                },
                allJourneys: {
                  title: 'إتمام جميع الرحلات',
                  description: 'أنهِ جميع الرحلات المسندة إليك.',
                },
                highAchiever: {
                  title: 'متفوق',
                  description: 'حافظ على متوسط درجات اختبارات 85% أو أعلى.',
                },
              },
            },
            quiz: {
              passMarkRequired:
                'درجة النجاح: {{passMark}}% — يجب أن تحصل على {{passMark}}% على الأقل لاجتياز هذا الاختبار.',
            },
            onboarding: {
              stepOfTotal: 'الخطوة {{current}} من {{total}}',
              continue: 'متابعة',
              later: 'لاحقاً',
              loading: 'جارٍ تحميل رحلة الإلحاق الخاصة بك…',
              noSteps: 'لم يتم إعداد خطوات الإلحاق بعد.',
              loadFailed: 'تعذّر تحميل رحلة الإلحاق. يرجى المحاولة مرة أخرى.',
              retry: 'إعادة المحاولة',
              video: {
                unavailable:
                  'هذا الفيديو غير متاح حالياً — يمكنك المتابعة عند جاهزيته.',
              },
              placeholder: {
                defaultTitle: 'خطوة الإلحاق',
                defaultDescription:
                  'ستتوفر هذه الخطوة قريباً. يمكنك المتابعة الآن.',
              },
              completion: {
                title: 'كل شيء جاهز!',
                subtitle: 'لقد أكملت رحلة الإلحاق الخاصة بك.',
                certificateLabel: 'شهادة إتمام',
                cta: 'وحدتك التالية جاهزة',
              },
              carousel: {
                watchedCount: 'تمت مشاهدة {{count}} من {{total}}',
              },
              cardsSign: {
                cardOf: 'البطاقة {{current}} من {{total}}',
                back: 'رجوع',
                next: 'التالي',
                signTitle: 'التوقيع للتأكيد',
                nameLabel: 'الاسم الكامل',
                attestLabel: 'لقد قرأت ووافقت على هذه المبادئ.',
                signAndContinue: 'توقيع ومتابعة',
              },
              cardsAccept: {
                cardOf: 'البطاقة {{current}} من {{total}}',
                back: 'رجوع',
                next: 'التالي',
                groupAcceptTitle: '{{group}}',
                acceptLabel: 'لقد قرأت ووافقت على {{group}}.',
              },
              quiz: {
                preparingTitle: 'يتم تجهيز تحدي الترحيب الخاص بك',
                preparingDescription:
                  'هذا الاختبار غير جاهز بعد — يمكنك المتابعة الآن وأخذه لاحقاً.',
                readyTitle: 'مستعد لتحدٍ سريع؟',
                passMark:
                  'ستحتاج إلى {{passMark}}% للنجاح — يمكنك إعادة المحاولة.',
                start: 'ابدأ الاختبار',
                encouragement: 'لا بأس — يمكنك المحاولة مرة أخرى كما تشاء.',
              },
              assessment: {
                preparingTitle: 'اكتشف نقاط قوتك — قريباً',
                defaultDescription:
                  'سيتوفر هذا التقييم قريباً. يمكنك المتابعة الآن.',
                passMark:
                  'ستحتاج إلى {{passMark}}% للنجاح — يمكنك إعادة المحاولة.',
                failedTitle: 'ليس تماماً — حاول مرة أخرى',
                failedScore:
                  'حصلت على {{score}}% — تحتاج إلى {{passMark}}% للنجاح.',
                questionCorrect: 'إجابة صحيحة',
                questionIncorrect: 'راجع هذا السؤال',
                retake: 'حاول مرة أخرى',
              },
              journey: {
                progress: 'اكتمل {{completed}} من {{total}} من العناصر',
                open: 'الانتقال إلى الرحلة',
                checkCompletion: 'التحقق من الإكمال',
                incomplete: 'أكمل جميع عناصر هذه الرحلة لإتمام هذه الخطوة.',
                completeReady: 'اكتملت الرحلة — يمكنك المتابعة.',
              },
              certificate: {
                title: 'اكتمل الإلحاق',
                cta: 'احصل على شهادتك',
              },
            },
            remediation: {
              title: 'هل تودّ بعض المساعدة هنا؟',
              body: 'الجميع يتعثر أحياناً. احصل على إرشاد دراسي قصير مبني على هذه المحاولة يساعدك على التركيز في محاولتك القادمة.',
              cta: 'احصل على الإرشاد',
              caption: 'إرشاد مُولّد تلقائياً — تحقق من أي معلومة مهمة.',
              limited:
                'لقد طلبت الإرشاد عدة مرات مؤخراً — يرجى المحاولة مجدداً بعد قليل.',
            },
          },
        },
      },
      interpolation: {
        format: (value, format, lng) => {
          if (!value) return;
          if (format === 'lowercase') {
            return value.toLowerCase();
          }
          if (format === 'firstLetterUpperCase') {
            return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
          }
          if (format === 'removeTheForArabic' && lng === 'ar') {
            return value.startsWith('ال') ? value.slice(2) : value;
          }
          if (format === 'addTheForArabic' && lng === 'ar') {
            return value.startsWith('ال') ? value : `ال${value}`;
          }
          return value;
        },
      },
    })
    .then(async () => {
      const organizationsStore = useOrganizationStore(pinia);
      const localStore = useLocaleStore(pinia);
      const vuetifyStore = useVuetifyStore(pinia);

      await organizationsStore.getOrganizationUsingUrl();
      await Promise.all([
        localStore.changeLocale(initialLang, i18next, false),
        organizationsStore.callOrganization(),
      ]);

      /*
       * Change theme colors and font family
       * based on organization colors and font family
       */
      const { organization } = storeToRefs(organizationsStore);
      if (organization.value) {
        vuetifyStore.changeThemeMainColors(
          organization.value?.colors[0].color,
          organization.value?.colors[1].color
        );
        vuetifyStore.changeFontFamily(organization.value?.fontFamilies);
        vuetifyStore.changeFavoriteIcon(organization.value?.icon?.url ?? '');
      }

      return i18next;
    });
}
